import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { buildContentPath, buildListPath } from "../utils/detailPaths";

/**
 * The single way anything opens a collection or content detail view.
 *
 * Passing the current location as `backgroundLocation` is what makes modals
 * chain: if the current location is itself a modal, its own `.state` already
 * carries its own `backgroundLocation`, so nesting falls out for free — see
 * AppRoutes' unwrapChain.
 */
export function useDetailNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const openContent = useCallback(
    ({ contentId, contentType, title, entryId }) => {
      const path = buildContentPath({ contentType, contentId, title });
      navigate(entryId ? `${path}?entry=${entryId}` : path, {
        state: { backgroundLocation: location },
      });
    },
    [navigate, location],
  );

  const openList = useCallback(
    // `type` (optional) opens a mixed list filtered to one content type via
    // ?type= — e.g. a profile's standouts from that content type's tab.
    (list, { type } = {}) => {
      // Curate's own grid already holds the full list (uncapped items, no
      // item_count) — seed it into ["list", id] so CollectionDetailRoute
      // paints from cache instead of re-fetching what we already have.
      // Guard against: Explore's carousel cards (capped items, item_count
      // set), Explore.js's {list_id, title} stub passed straight from the
      // section-page card, and clobbering a detail fetch already in cache.
      const isFullListPayload =
        Array.isArray(list.items) && list.item_count == null;
      if (
        isFullListPayload &&
        !queryClient.getQueryData(["list", list.list_id])
      ) {
        queryClient.setQueryData(["list", list.list_id], list);
      }
      const path = buildListPath(list);
      navigate(type ? `${path}?type=${type}` : path, {
        state: { backgroundLocation: location },
      });
    },
    [navigate, location, queryClient],
  );

  // navigate(-1) keeps Back and the close button in agreement. Arriving from a
  // shared link has no in-app history to pop to, so close there lands on
  // Explore instead of leaving the visitor on a blank page.
  const closeDetail = useCallback(() => {
    if (location.key === "default") {
      navigate("/explore", { replace: true });
    } else {
      navigate(-1);
    }
  }, [navigate, location]);

  return { openContent, openList, closeDetail };
}
